import type { JournalEntry, JournalLine } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  DateInput,
  Dialog,
  Drawer,
  FormField,
  MoneyText,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BadgeCheck, Copy, Layers, Pencil, Trash2, Undo2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountingPage, companySearchShape } from "../../../../../features/accounting/company.js";
import {
  Amount,
  EntryStatusBadge,
  PERIOD_STATUS_TONE,
  todayIstanbul,
  useJournalEntry,
} from "../../../../../features/accounting/shared.js";
import { useCounterpartyOption } from "../../../../../features/counterparties/counterparty-combobox.js";
import { api, ApiError } from "../../../../../lib/api.js";
import { errorMessage } from "../../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../../lib/format.js";
import { useDocumentTitle } from "../../../../../lib/hooks.js";
import { queryKeys } from "../../../../../lib/query.js";
import { useMe, usePermissions } from "../../../../../lib/session.js";

export const Route = createFileRoute("/_app/muhasebe/fisler/$id/")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: EntryDetailPage,
});

function entryTitle(
  entry: JournalEntry,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return entry.entryNo
    ? t("accounting.detail.title", { entryNo: entry.entryNo, year: entry.fiscalYear })
    : t("accounting.detail.draftTitle");
}

function EntryDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const entry = useJournalEntry(id);
  // Kesinleştirme reddedilirse satıra bağlanabilen hatalar tabloda gösterilir.
  const [postError, setPostError] = useState<unknown>(null);
  useDocumentTitle(t("menu.journalEntries"));

  if (!entry.data)
    return (
      <>
        <PageHeader
          title={t("menu.journalEntries")}
          breadcrumb={[{ label: t("menu.groups.accounting") }, { label: t("menu.journalEntries") }]}
        />
        {entry.error ? <Alert tone="danger">{errorMessage(entry.error)}</Alert> : <SkeletonRows rows={8} />}
      </>
    );

  const data = entry.data;
  return (
    <AccountingPage
      fixedCompanyId={data.companyId}
      title={entryTitle(data, t)}
      description={data.description}
      breadcrumb={[
        {
          label: t("menu.journalEntries"),
          link: (children, className) => (
            <Link to="/muhasebe/fisler" className={className}>
              {children}
            </Link>
          ),
        },
        { label: entryTitle(data, t) },
      ]}
      actions={<EntryActions entry={data} onPostError={setPostError} />}
    >
      {() => <EntryDetail key={`${data.id}-${data.version}`} entry={data} postError={postError} />}
    </AccountingPage>
  );
}

/** Sunucunun `lines.N.alan` yolundaki hatalarını satır sırasına göre gruplar (kesinleştirme reddedilince). */
function lineErrorsOf(error: unknown): Map<number, string[]> {
  const byLine = new Map<number, string[]>();
  if (!(error instanceof ApiError)) return byLine;
  for (const fieldError of error.problem.errors ?? []) {
    const match = /^(?:body\.)?lines\.(\d+)\./.exec(fieldError.path);
    if (!match) continue;
    const index = Number(match[1]);
    byLine.set(index, [...(byLine.get(index) ?? []), fieldError.message]);
  }
  return byLine;
}

function EntryActions({
  entry,
  onPostError,
}: {
  entry: JournalEntry;
  onPostError: (error: unknown) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [deleting, setDeleting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reverseDate, setReverseDate] = useState<string | null>(todayIstanbul());
  const [reverseDescription, setReverseDescription] = useState("");
  const [reverseError, setReverseError] = useState<string | null>(null);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.accounting(entry.companyId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.accountingEntry() }),
    ]);

  const post = useMutation({
    mutationFn: async () =>
      api.POST("/accounting/journal-entries/{id}/post", {
        params: { path: { id: entry.id } },
        body: { version: entry.version },
      }),
    onMutate: () => onPostError(null),
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("accounting.entry.toast.posted") });
    },
    onError: (error) => {
      onPostError(error);
      toast({ tone: "danger", title: errorMessage(error) });
    },
  });
  const reverse = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/accounting/journal-entries/{id}/reverse", {
          params: { path: { id: entry.id } },
          body: { postingDate: reverseDate!, description: reverseDescription.trim() || null },
        })
      ).data!,
    onSuccess: async (result) => {
      setReversing(false);
      await refresh();
      toast({ tone: "success", title: t("accounting.detail.reversed") });
      if (result.id !== entry.id) await navigate({ to: "/muhasebe/fisler/$id", params: { id: result.id } });
    },
    onError: (error) => setReverseError(errorMessage(error)),
  });

  const isDraft = entry.status === "DRAFT";
  const canManage = permissions.has("accounting.entry.manage");
  return (
    <>
      {canManage ? (
        <Button variant="outline" asChild>
          <Link to="/muhasebe/fisler/yeni" search={{ copy: entry.id }}>
            <Copy aria-hidden="true" />
            {t("accounting.detail.copy")}
          </Link>
        </Button>
      ) : null}
      {isDraft && canManage ? (
        <>
          <Button variant="outline" onClick={() => setDeleting(true)}>
            <Trash2 aria-hidden="true" />
            {t("accounting.detail.delete")}
          </Button>
          <Button variant="outline" asChild>
            <Link to="/muhasebe/fisler/$id/duzenle" params={{ id: entry.id }}>
              <Pencil aria-hidden="true" />
              {t("common.edit")}
            </Link>
          </Button>
        </>
      ) : null}
      {isDraft && permissions.has("accounting.entry.post") ? (
        <Button onClick={() => post.mutate()} loading={post.isPending}>
          <BadgeCheck aria-hidden="true" />
          {t("accounting.detail.post")}
        </Button>
      ) : null}
      {entry.status === "POSTED" && permissions.has("accounting.entry.reverse") ? (
        <Button
          variant="outline"
          onClick={() => {
            setReverseError(null);
            setReversing(true);
          }}
        >
          <Undo2 aria-hidden="true" />
          {t("accounting.detail.reverse")}
        </Button>
      ) : null}

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        tone="danger"
        title={t("accounting.detail.deleteTitle")}
        description={t("accounting.detail.deleteDescription")}
        confirmLabel={t("accounting.detail.delete")}
        onConfirm={async () => {
          try {
            await api.DELETE("/accounting/journal-entries/{id}", { params: { path: { id: entry.id } } });
            await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(entry.companyId) });
            toast({ tone: "success", title: t("accounting.detail.deleted") });
            await navigate({ to: "/muhasebe/fisler" });
          } catch (error) {
            toast({ tone: "danger", title: errorMessage(error) });
            throw error;
          }
        }}
      />

      <Dialog
        open={reversing}
        onOpenChange={setReversing}
        title={t("accounting.detail.reverseTitle")}
        description={t("accounting.detail.reverseDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setReverseError(null);
            if (reverseDate) reverse.mutate();
          }}
        >
          {reverseError ? <Alert tone="danger">{reverseError}</Alert> : null}
          <FormField
            label={t("accounting.detail.reverseDate")}
            description={t("accounting.detail.reverseDateHint")}
            required
          >
            {(control) => (
              <DateInput {...control} value={reverseDate} onValueChange={setReverseDate} autoFocus />
            )}
          </FormField>
          <FormField
            label={t("accounting.fields.description")}
            description={t("accounting.detail.reverseDescriptionHint")}
          >
            {(control) => (
              <Textarea
                {...control}
                rows={2}
                maxLength={500}
                value={reverseDescription}
                onChange={(event) => setReverseDescription(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReversing(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={reverse.isPending} disabled={!reverseDate}>
              {t("accounting.detail.reverseConfirm")}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

/**
 * Satırın cari boyutu: fiş yanıtı yalnızca kimlik taşır; ad cari modülünden çözülür ve karta bağlanır. Cari modülü
 * kapalıysa ya da kart okuma izni yoksa kimlik düz metin gösterilir.
 */
function LineCounterparty({ id }: { id: string }): ReactElement {
  const { t } = useTranslation();
  const features = useMe().data?.activeTenant?.features ?? [];
  const permissions = usePermissions();
  const canRead =
    (features as readonly string[]).includes("CURRENT_ACCOUNTS") && permissions.has("counterparty.card.read");
  const { option } = useCounterpartyOption(canRead ? id : null);
  const label = t("accounting.dimensions.COUNTERPARTY");
  if (!option)
    return (
      <span>
        {label}: {id}
      </span>
    );
  return (
    <span>
      {label}:{" "}
      <Link
        to="/cari/$id"
        params={{ id }}
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {option.code} {option.name}
      </Link>
    </span>
  );
}

function EntryDetail({ entry, postError }: { entry: JournalEntry; postError: unknown }): ReactElement {
  const { t } = useTranslation();
  const [sourcesOf, setSourcesOf] = useState<JournalLine | null>(null);
  const lineErrors = lineErrorsOf(postError);

  const facts: Array<[string, ReactElement | string]> = [
    [t("accounting.fields.entryType"), t(`enums.journalEntryType.${entry.entryType}`)],
    [t("accounting.fields.postingDate"), formatDate(entry.postingDate)],
    [t("accounting.fields.documentDate"), formatDate(entry.documentDate)],
    [t("accounting.fields.fiscalYear"), String(entry.fiscalYear)],
    [
      t("accounting.fields.periodStatus"),
      <StatusBadge key="period" tone={PERIOD_STATUS_TONE[entry.periodStatus]}>
        {t(`enums.fiscalPeriodStatus.${entry.periodStatus}`)}
      </StatusBadge>,
    ],
    [t("accounting.fields.source"), t(`enums.journalSource.${entry.source}`, { defaultValue: entry.source })],
    // Kaynak modüller (kasa, fatura…) henüz yok: belge bağlantısı yerine tür ve kimlik düz metin gösterilir.
    [
      t("accounting.fields.sourceDocument"),
      entry.sourceDocType
        ? `${entry.sourceDocType}${entry.sourceDocId ? ` · ${entry.sourceDocId}` : ""}`
        : "—",
    ],
    [t("accounting.fields.createdBy"), entry.createdByName ?? "—"],
    [t("accounting.fields.createdAt"), formatDateTime(entry.createdAt)],
    [t("accounting.fields.postedAt"), formatDateTime(entry.postedAt)],
  ];

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <EntryStatusBadge status={entry.status} />
        {entry.isSummary ? (
          <StatusBadge tone="info">
            <Layers className="size-3" aria-hidden="true" />
            {t("accounting.detail.summaryEntry")}
          </StatusBadge>
        ) : null}
        {entry.entryNo ? (
          <span className="text-sm text-muted-foreground">
            {t("accounting.fields.entryNo")}:{" "}
            <span className="font-mono font-semibold text-foreground">{entry.entryNo}</span>
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">{t("accounting.detail.draftHint")}</span>
        )}
      </div>

      {entry.reversalOfId ? (
        <Alert tone="info">
          {t("accounting.detail.reversalOf")}{" "}
          <Link
            to="/muhasebe/fisler/$id"
            params={{ id: entry.reversalOfId }}
            className="font-medium text-primary underline underline-offset-4"
          >
            {t("accounting.detail.openOriginal")}
          </Link>
        </Alert>
      ) : null}
      {entry.reversedById ? (
        <Alert tone="warning">
          {t("accounting.detail.reversedBy")}{" "}
          <Link
            to="/muhasebe/fisler/$id"
            params={{ id: entry.reversedById }}
            className="font-medium text-primary underline underline-offset-4"
          >
            {t("accounting.detail.openReversal")}
          </Link>
        </Alert>
      ) : null}
      {postError ? <Alert tone="danger">{errorMessage(postError)}</Alert> : null}

      <Card className="p-4 sm:p-5">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-5">
          {facts.map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="break-words font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t("accounting.detail.linesCaption")}</caption>
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.entry.lineNo")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.account")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.description")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.dimensions")}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                {t("accounting.fields.document")}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                {t("accounting.fields.debit")}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                {t("accounting.fields.credit")}
              </th>
            </tr>
          </thead>
          <tbody>
            {entry.lines.map((line, index) => {
              const dimensions = [
                line.stationName,
                line.businessUnit ? t(`enums.businessUnit.${line.businessUnit}`) : null,
                line.costCenterName,
                line.instrumentId ? `${t("accounting.dimensions.INSTRUMENT")}: ${line.instrumentId}` : null,
              ].filter(Boolean);
              const documentInfo = [line.docType, line.docNo, line.docDate ? formatDate(line.docDate) : null]
                .filter(Boolean)
                .join(" · ");
              const errors = lineErrors.get(index) ?? [];
              return (
                <tr key={line.id} className="border-t align-top">
                  <th scope="row" className="px-3 py-2 text-left text-xs font-medium tabular">
                    {line.lineNo}
                  </th>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs font-semibold">{line.accountCode}</span>{" "}
                    <span>{line.accountName}</span>
                    {errors.length > 0 ? (
                      <ul role="alert" className="mt-1 grid gap-0.5 text-xs font-medium text-destructive">
                        {errors.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{line.description ?? "—"}</td>
                  <td className="px-3 py-2">
                    {dimensions.length > 0 || line.counterpartyId ? dimensions.join(" · ") : "—"}
                    {line.counterpartyId ? (
                      <>
                        {dimensions.length > 0 ? " · " : ""}
                        <LineCounterparty id={line.counterpartyId} />
                      </>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {documentInfo || "—"}
                    {line.sourceCount > 0 ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto px-0 py-0.5"
                        onClick={() => setSourcesOf(line)}
                      >
                        {t("accounting.detail.sources", { count: line.sourceCount })}
                      </Button>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={line.debit} blankZero />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={line.credit} blankZero />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t bg-muted/40 font-semibold">
            <tr>
              <th scope="row" colSpan={5} className="px-3 py-2 text-right">
                {t("accounting.fields.total")}
              </th>
              <td className="px-3 py-2 text-right">
                <MoneyText value={entry.totalDebit} />
              </td>
              <td className="px-3 py-2 text-right">
                <MoneyText value={entry.totalCredit} />
              </td>
            </tr>
          </tfoot>
        </table>
      </Card>
      {entry.status === "DRAFT" && entry.totalDebit !== entry.totalCredit ? (
        <Alert tone="warning">{t("accounting.detail.unbalancedDraft")}</Alert>
      ) : null}

      <Drawer
        open={sourcesOf !== null}
        onOpenChange={(open) => !open && setSourcesOf(null)}
        title={t("accounting.detail.sourcesTitle")}
        {...(sourcesOf ? { description: `${sourcesOf.accountCode} ${sourcesOf.accountName}` } : {})}
      >
        {sourcesOf ? <LineSources companyId={entry.companyId} lineId={sourcesOf.id} /> : null}
      </Drawer>
    </div>
  );
}

/** Özet fiş satırını oluşturan kaynak işlemler (drill-down). Kaynak modüller gelene kadar tür + kimlik gösterilir. */
function LineSources({ companyId, lineId }: { companyId: string; lineId: string }): ReactElement {
  const { t } = useTranslation();
  const sources = useQuery({
    queryKey: queryKeys.accounting(companyId, "line-sources", lineId),
    queryFn: async () =>
      (await api.GET("/accounting/journal-lines/{id}/sources", { params: { path: { id: lineId } } })).data!
        .items,
  });
  if (sources.error) return <Alert tone="danger">{errorMessage(sources.error)}</Alert>;
  if (!sources.data) return <SkeletonRows rows={4} />;
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <caption className="sr-only">{t("accounting.detail.sourcesTitle")}</caption>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 text-left">
              {t("accounting.detail.sourceType")}
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              {t("accounting.detail.sourceId")}
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              {t("accounting.fields.amount")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sources.data.map((source) => (
            <tr key={`${source.sourceType}:${source.sourceId}`} className="border-t">
              <td className="px-3 py-2">{source.sourceType}</td>
              <td className="px-3 py-2 font-mono text-xs">{source.sourceId}</td>
              <td className="px-3 py-2 text-right">
                <MoneyText value={source.amount} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
